import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { UzButton } from '@utazzo/ui';
import React, { useMemo, useState } from 'react';

import { clampDepth, type FlatDraftItem } from './navigation-tree.utils';
import { WpMenuItemCard } from './WpMenuItemCard';

interface Props {
  items: FlatDraftItem[];
  canWrite: boolean;
  onChange: (items: FlatDraftItem[]) => void;
}

/** WordPress-style right column: drag-to-reorder menu structure with nesting. */
export const MenuStructurePanel = ({ items, canWrite, onChange }: Props): React.JSX.Element => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());

  const visible = useMemo(() => items.filter((i) => !i.isDeleted), [items]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const patchItem = (id: string, patch: Partial<FlatDraftItem>): void => {
    onChange(items.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const removeItem = (id: string): void => {
    onChange(items.map((i) => (i.id === id ? { ...i, isDeleted: true } : i)));
    if (expandedId === id) setExpandedId(null);
  };

  const indentItem = (id: string): void => {
    const index = items.findIndex((i) => i.id === id);
    if (index < 0) return;
    const next = items.map((i) =>
      i.id === id ? { ...i, depth: clampDepth(items, index, i.depth + 1) } : i,
    );
    onChange(next);
  };

  const outdentItem = (id: string): void => {
    onChange(items.map((i) => (i.id === id ? { ...i, depth: Math.max(0, i.depth - 1) } : i)));
  };

  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = visible.findIndex((i) => i.id === active.id);
    const to = visible.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;

    const reorderedVisible = arrayMove(visible, from, to);
    const deleted = items.filter((i) => i.isDeleted);
    onChange([...reorderedVisible, ...deleted]);
  };

  const bulkRemove = (): void => {
    if (bulkSelected.size === 0) return;
    onChange(items.map((i) => (bulkSelected.has(i.id) ? { ...i, isDeleted: true } : i)));
    setBulkSelected(new Set());
    setBulkMode(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-(--fg)">Menu structure</h2>
          <p className="mt-1 text-xs text-(--fg-faint)">
            Drag items into the order you prefer. Click the arrow to edit. Use Indent / Outdent to
            nest sub-items.
          </p>
        </div>
        {canWrite && visible.length > 0 && (
          <UzButton
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setBulkMode((v) => !v);
              setBulkSelected(new Set());
            }}
          >
            {bulkMode ? 'Cancel' : 'Bulk Select'}
          </UzButton>
        )}
      </div>

      {bulkMode && bulkSelected.size > 0 && (
        <div className="flex items-center gap-2 rounded border border-(--border) bg-(--z-50) px-3 py-2">
          <span className="text-xs text-(--fg-faint)">{bulkSelected.size} selected</span>
          <UzButton type="button" size="sm" variant="outline" onClick={bulkRemove}>
            Remove selected
          </UzButton>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="rounded border border-dashed border-(--border-strong) py-12 text-center text-sm text-(--fg-faint)">
          Add menu items from the left column, then click Save Menu below.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={visible.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-1.5">
              {visible.map((item, index) => (
                <WpMenuItemCard
                  key={item.id}
                  item={item}
                  canWrite={canWrite}
                  expanded={expandedId === item.id}
                  bulkMode={bulkMode}
                  bulkSelected={bulkSelected.has(item.id)}
                  onToggleExpand={() => setExpandedId((id) => (id === item.id ? null : item.id))}
                  onToggleBulk={() =>
                    setBulkSelected((s) => {
                      const n = new Set(s);
                      if (n.has(item.id)) n.delete(item.id);
                      else n.add(item.id);
                      return n;
                    })
                  }
                  onChange={(patch) => patchItem(item.id, patch)}
                  onRemove={() => removeItem(item.id)}
                  onIndent={() => indentItem(item.id)}
                  onOutdent={() => outdentItem(item.id)}
                  canIndent={index > 0}
                  canOutdent={item.depth > 0}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
